/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Composer from 'cio/components/action-composer/types/email/text-and-code';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { isNone } from '@ember/utils';
import { dropTask } from 'ember-concurrency';
import { resolve } from 'rsvp';
import { liquidTag } from 'cio/helpers/liquid-tag';
import ApiErrorsService from 'cio/services/api-errors';
import BeeService from 'cio/services/bee';
import EventBusService from 'cio/services/event-bus';
import EnvironmentService from 'cio/services/environment';
import { MergeTag } from 'cio/utils/editor-utils';
import ObjectTypeModel from 'cio/models/object-type';
import RelationshipModel from 'cio/models/relationship';

export default class DragAndDrop extends Composer {
  @service apiErrors!: ApiErrorsService;
  @service bee!: BeeService;
  @service eventBus!: EventBusService;
  @service environment!: EnvironmentService;

  @tracked hasLoadedMergeData = false;
  @tracked isShowingMergeData = false;

  @computed(
    'args.hasReadOnlyPermission',
    'isDirty',
    'isInvalid',
    'bee.{hasLoadedContent,isFetchingHTML}',
    'saveTask.isRunning',
  )
  get saveDisabled() {
    return (
      this.args.hasReadOnlyPermission ||
      !this.isDirty ||
      this.isInvalid ||
      !this.bee.hasLoadedContent ||
      this.saveTask.isRunning ||
      this.bee.isFetchingHTML
    );
  }

  @computed('args.hasReadOnlyPermission', 'saveTooltipContent', 'isDirty', 'bee.isFetchingHTML')
  get saveDisabledTooltipContent() {
    // Drag and drop extends from the text and code component. If the headers
    // have errors or the subject is empty, text and code will return the
    // correct error, so we can just use that.
    if (this.saveTooltipContent) {
      return this.saveTooltipContent;
    }

    if (!this.isDirty) {
      return 'No changes to save!';
    }

    if (this.bee.isFetchingHTML) {
      return 'Updating HTML…';
    }

    if (this.args.hasReadOnlyPermission) {
      return "You don't have write permissions.";
    }

    return '';
  }

  @computed('environment.{sentAttributes,snippets}', 'args.{selectedEvent.attrs,relatedObjects}')
  get mergeTags() {
    let attrs: MergeTag[] = [];

    let customerAttrs = this.environment.sentAttributes;
    let eventAttrs = this.args.selectedEvent?.attrs;
    let { relatedObjects } = this.args;

    let liquidTagWithPrefix = (prefix: string | (string | number)[], attr: string) => {
      let name = liquidTag([prefix, attr]);

      return {
        name,
        value: `{{${name}}}`,
      };
    };

    if (eventAttrs) {
      Object.keys(eventAttrs).forEach((attr) => {
        attrs.push(liquidTagWithPrefix('event', attr));
      });
    }

    if (customerAttrs) {
      customerAttrs.forEach((attr) => {
        attrs.push(liquidTagWithPrefix('customer', attr));
      });
    }

    if (this.environment.snippets) {
      this.environment.snippets.forEach((snippet) => {
        attrs.push(liquidTagWithPrefix('snippets', snippet.name));
      });
    }

    if (relatedObjects?.length > 0) {
      relatedObjects.forEach((relatedObject, index: number) => {
        let { slug } = relatedObject.belongsTo('objectType').value() as ObjectTypeModel;
        let relationships = relatedObject.hasMany('relationships').value() as RelationshipModel[] | null;
        let liquidPath = ['objects', slug, index];

        attrs.push(...Object.keys(relatedObject.attributes).map((key) => liquidTagWithPrefix(liquidPath, key)));

        if (relationships != null) {
          let relationshipLiquidPath = [...liquidPath, 'relationship'];

          Array.from(relationships).forEach((relationship) => {
            attrs.push(
              ...Object.keys(relationship.attributes).map((key) => liquidTagWithPrefix(relationshipLiquidPath, key)),
            );
          });
        }
      });
    }

    // this code would execute for object/relationships campaigns
    let objectType = this.args.editableEntity.campaign?.objectTypeForCampaign;
    if (objectType != null) {
      customerAttrs.forEach((attr) => {
        attrs.push(liquidTagWithPrefix(['trigger', 'customer'], attr));
      });

      let object = this.args.selectedObject;

      if (object == null) {
        object = relatedObjects.find((object) => {
          let { id } = object.belongsTo('objectType').value() as ObjectTypeModel;
          if (id === objectType!.id) {
            return object;
          }
          return false;
        });
      }

      if (object != null) {
        attrs.push(
          ...Object.keys(object.attributes).map((key) => liquidTagWithPrefix(['trigger', objectType!.slug], key)),
        );

        let relationships = object?.hasMany('relationships').value() as RelationshipModel[] | null;

        if (relationships != null) {
          Array.from(relationships).forEach((relationship) => {
            attrs.push(
              ...Object.keys(relationship.attributes).map((key) =>
                liquidTagWithPrefix(['trigger', 'relationship'], key),
              ),
            );
          });
        }
      }
    }

    return attrs;
  }

  @action
  didLoadMergeData() {
    this.hasLoadedMergeData = true;
    this.fetchPreview();
  }

  @action
  rollback() {
    this.template.rollbackAttributes();
  }

  @action
  save() {
    return this.saveTask.perform();
  }

  saveTask = dropTask(async () => {
    if (isNone(this.bee.getCurrentInstance())) {
      return resolve();
    }

    this.removeEmptyHeaders();

    let { json, html } = await this.bee.save();
    this.template.setProperties({
      body: html,
      bodyJson: json,
      layout: null,
      disablePreprocessor: true,
      editor: 'bee',
    });

    let promise = this.template.save();

    this.apiErrors.attach(promise);
    this.cacheOriginalRelationships();

    return promise;
  });
}
